import { ColDef } from "ag-grid-community";

import { ColumnType } from "components/AgGrid/constants";
import { NameType } from "constants/integrationBusiness";
import { caseInsensitiveCompare } from "lib/compare";
import {
  formatIntegrationBusinessAddress,
  formatIntegrationBusinessCollection,
  formatIntegrationBusinessContact,
  formatIntegrationBusinessPhone,
} from "lib/agGrid/formatters";
import { XeroContactLinkRenderer } from "lib/agGrid/renderers";

export const IntegrationBusinessColId = {
  NAMES: "4a1700dc-39c4-411b-8f06-ad7cd59448ce",
  ADDRESSES: "6c046f31-b4c2-424b-8a60-203b6a1e2571",
  CONTACTS: "eb3239c3-843d-40ff-a5af-59044ba8d944",
  PHONES: "d7818df0-df29-449e-ba3d-b95e59c7bc8d",
  IS_ACTIVE: "afc6e56c-5b4f-47d4-a7c7-1619454416b5",
  XERO_CONTACT_LINK: "c8f2a1d3-7e94-4b6a-9f1c-e3d8b2a50f7e",
} as const;

type NameItem = { type: string; value: string };
type Typed = { type: string };

function primaryOrFirst<T extends Typed>(
  completed: Array<[T, string]>,
): string {
  if (completed.length === 0) {
    return "";
  }
  if (completed.length === 1) {
    return completed[0][1];
  }
  const first =
    completed.find(([item]) => item.type === "Primary") || completed[0];
  return `${first[1]}; +${completed.length - 1} more...`;
}

export const IntegrationBusinessColumnDefinitions = {
  [IntegrationBusinessColId.NAMES]: {
    colId: IntegrationBusinessColId.NAMES,
    comparator: caseInsensitiveCompare,
    headerName: "Name",
    width: 300,
    valueGetter: params => {
      const names: NameItem[] = params.data?.names || [];
      if (names.length === 0) {
        return "";
      }
      return names.find(n => n.type === NameType.NAME)?.value || names[0].value;
    },
  },

  [IntegrationBusinessColId.ADDRESSES]: {
    colId: IntegrationBusinessColId.ADDRESSES,
    comparator: caseInsensitiveCompare,
    headerName: "Address",
    width: 280,
    valueGetter: params => {
      const addresses: unknown[] = params.data?.addresses || [];
      return primaryOrFirst(
        formatIntegrationBusinessCollection(
          formatIntegrationBusinessAddress,
          addresses,
        ) as Array<[Typed, string]>,
      );
    },
  },

  [IntegrationBusinessColId.CONTACTS]: {
    colId: IntegrationBusinessColId.CONTACTS,
    comparator: caseInsensitiveCompare,
    headerName: "Contact",
    valueGetter: params => {
      const contacts: unknown[] = params.data?.contacts || [];
      return primaryOrFirst(
        formatIntegrationBusinessCollection(
          formatIntegrationBusinessContact,
          contacts,
        ) as Array<[Typed, string]>,
      );
    },
  },

  [IntegrationBusinessColId.PHONES]: {
    colId: IntegrationBusinessColId.PHONES,
    comparator: caseInsensitiveCompare,
    headerName: "Phone",
    width: 160,
    valueGetter: params => {
      const phones: unknown[] = params.data?.phones || [];
      return primaryOrFirst(
        formatIntegrationBusinessCollection(
          formatIntegrationBusinessPhone,
          phones,
        ) as Array<[Typed, string]>,
      );
    },
  },

  [IntegrationBusinessColId.IS_ACTIVE]: {
    colId: IntegrationBusinessColId.IS_ACTIVE,
    field: "is_active",
    headerName: "Active",
    width: 160,
    type: ColumnType.BOOLEAN,
    enableRowGroup: true,
  },

  [IntegrationBusinessColId.XERO_CONTACT_LINK]: {
    colId: IntegrationBusinessColId.XERO_CONTACT_LINK,
    headerName: "Xero Contact",
    width: 130,
    cellRenderer: XeroContactLinkRenderer,
    sortable: false,
    filter: false,
  },
} satisfies Record<ValueInObj<typeof IntegrationBusinessColId>, ColDef>;
